/*@flow*/
import type { Node } from 'react';
import React, { Fragment } from 'react';
import * as t from 'lib/t';
import type { StructType } from 'lib/t/types';
import cx from 'classnames';
import Breakpoints from '../util/Breakpoints';
import LabelComponent from './Label';
import ErrorComponent from './ErrorComponent';
import Help from './Help';
import FormGroup from './FormGroup';
import VerticalCols from './VerticalCols';
import type { LocalsT, RadioLocals } from '../types';
import { SectionBefore, SectionAfter } from './Sections';

const RadioConfig: StructType = (t.struct(
  {
    horizontal: t.maybe(Breakpoints),
    vertical: t.maybe(Breakpoints),
  },
  'RadioConfig'
): any);

type PropsT = LocalsT & RadioLocals;

const getConfig = (locals: PropsT) => RadioConfig(locals.config || {});

type Registry = {|
  Radios: React$StatelessFunctionalComponent<*>,
  Label: React$StatelessFunctionalComponent<*>,
  Help: React$StatelessFunctionalComponent<*>,
  Vertical: React$StatelessFunctionalComponent<*>,
  Horizontal: React$StatelessFunctionalComponent<*>,
  FormGroup: React$StatelessFunctionalComponent<*>,
|};

const clone = (overrides: $Shape<Registry> = {}) => {
  let registry = {};

  function RadioItem({ attrs, text }) {
    return (
      <div
        className={cx({
          'form-check': true,
          disabled: attrs.disabled,
        })}
      >
        <input {...attrs} className={cx('form-check-input', attrs.className)} />
        <label
          htmlFor={attrs.id}
          className={cx('form-check-label ml-2', attrs.className)}
        >
          {text}
        </label>
      </div>
    );
  }

  function Radios(locals: PropsT) {
    const onChange = (evt) => locals.onChange(evt.target.value);

    const fn = (option, i) => {
      const type = 'radio';

      const checked = option.value === locals.value;
      const disabled = locals.disabled;
      const value = option.value;
      const autoFocus = locals.attrs.autoFocus && i === 0;
      const id = `${locals.attrs.id}_${i}`;

      let describedby = locals.attrs['aria-describedby'];
      if (!describedby && locals.label) {
        describedby = id;
      }

      const attrs = {
        ...locals.attrs,
        id,
        type,
        checked,
        disabled,
        value,
        autoFocus,
        'aria-describedby': describedby,
        onChange,
      };

      return (
        <RadioItem
          {...{
            attrs,
            text: option.text,
            key: option.value,
          }}
        />
      );
    };
    return locals.options.map(fn);
  }

  function Label(locals: PropsT) {
    return (
      <LabelComponent
        {...{
          label: locals.label,
          htmlFor: locals.attrs.id,
          breakpoints: locals.config.horizontal,
          className: cx({ 'text-danger font-weight-bold': locals.hasError }),
        }}
      />
    );
  }

  function Vertical(locals: PropsT) {
    return (
      <Fragment>
        <registry.Label {...locals} />
        <VerticalCols {...locals}>
          <registry.Radios {...locals} />
          <ErrorComponent {...locals} />
          <registry.Help {...locals} />
        </VerticalCols>
      </Fragment>
    );
  }

  function Horizontal(locals: PropsT) {
    const className = locals.label
      ? locals.config.horizontal.getInputClassName()
      : locals.config.horizontal.getOffsetClassName();
    return (
      <div className="form-row">
        <registry.Label {...locals} />
        <div className={cx(className, 'pt-2')}>
          <registry.Radios {...locals} />
          <ErrorComponent {...locals} />
          <registry.Help {...locals} />
        </div>
        ,
      </div>
    );
  }

  function RadioComponent(locals: PropsT) {
    let locals2 = { ...locals };
    locals2.config = getConfig(locals2);

    const children = locals2.config.horizontal ? (
      <registry.Horizontal {...locals2} />
    ) : (
      <registry.Vertical {...locals2} />
    );

    return (
      <Fragment>
        <registry.SectionBefore {...locals} />
        <registry.FormGroup {...locals2}>{children}</registry.FormGroup>
        <registry.SectionAfter {...locals} />
      </Fragment>
    );
  }

  RadioComponent.clone = clone;

  registry = {
    Radios,
    Label,
    Help,
    Vertical,
    Horizontal,
    FormGroup,
    SectionBefore,
    SectionAfter,
    ...overrides,
  };

  return RadioComponent;
};

export default (clone(): (locals: PropsT) => Node);
